/**
 * resolvePremium — the single rule for "is this user premium right now?"
 *
 * Pure function. No Firestore reads, no side effects. Input is plain data
 * gathered by the hook/caller; output is a plain object the UI can switch on.
 *
 * Model: docs/MONETIZATION_MODEL.md — "How 'is this user premium right now?'
 * gets resolved" is the spec this implements.
 *
 * The check is context-aware: the answer depends on the current schoolId and
 * role, not just the user. Switching school workspace can change the answer
 * with no subscription change.
 */

/**
 * @typedef {Object} ResolveInput
 * @property {string|null} schoolId          Current workspace (null = Personal)
 * @property {string} role                   'parent'|'teacher'|'admin'|'dev'
 * @property {Object|null} customerInfo      RevenueCat CustomerInfo — individual sub
 * @property {Object|null} schoolSub         { tier, status } — school's Stripe sub
 * @property {Object|null} userSeat          { active, aiSeat } — user's seat in this school
 *
 * @typedef {Object} ResolveResult
 * @property {boolean} isPremium
 * @property {'individual'|'school'|null} source
 * @property {string|null} schoolId          Set when source === 'school'
 * @property {string} tier                   'free'|'basic'|'pro'|'teacher_pro'|'parent_pro'
 * @property {boolean} hasAi                 Individual teacher_pro OR school AI seat
 */

/**
 * @param {ResolveInput} input
 * @returns {ResolveResult}
 */
export function resolvePremium({ schoolId, role, customerInfo, schoolSub, userSeat }) {
  const indEntitlement = role === 'parent' ? 'parent_premium' : 'teacher_premium';
  const indActive = !!customerInfo?.entitlements?.active?.[indEntitlement];

  if (indActive) {
    return {
      isPremium: true,
      source: 'individual',
      schoolId: null,
      tier: role === 'parent' ? 'parent_pro' : 'teacher_pro',
      hasAi: role !== 'parent',
    };
  }

  const schoolTier = schoolSub?.tier;
  const schoolActive = schoolSub?.status === 'active';
  const seatActive = !!userSeat?.active;
  const inPaidSchoolContext =
    !!schoolId && schoolTier && schoolTier !== 'free' && schoolActive && seatActive;

  if (inPaidSchoolContext) {
    return {
      isPremium: true,
      source: 'school',
      schoolId,
      tier: schoolTier,
      hasAi: !!userSeat?.aiSeat,
    };
  }

  return {
    isPremium: false,
    source: null,
    schoolId: null,
    tier: 'free',
    hasAi: false,
  };
}

/**
 * Evaluate whether a feature registry entry is satisfied by a resolved status.
 * See features.js for the `requires` vocabulary.
 *
 * @param {string} requires
 * @param {ResolveResult} status
 */
export function satisfies(requires, status) {
  switch (requires) {
    case 'premium':
      return status.isPremium;
    case 'school.pro':
      return status.source === 'school' && status.tier === 'pro';
    case 'school.basic_or_pro':
      return status.source === 'school' && (status.tier === 'basic' || status.tier === 'pro');
    case 'ai_seat':
      return status.hasAi;
    case 'teacher.pro':
      return status.tier === 'teacher_pro' || (status.source === 'school' && status.tier === 'pro');
    case 'parent.pro':
      return status.tier === 'parent_pro';
    default:
      return false;
  }
}
